import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowDown,
    CalendarDays,
    CarFront,
    Mail,
    MapPin,
    Phone,
    Star,
    Sun,
    Umbrella,
    Users,
    Waves,
    Wifi,
    Umbrella as BeachIcon,
    BedDouble,
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { loadRooms } from "@/data/roomStore";
import { reviews, averageRating } from "@/data/reviews";
import { useLang } from "@/i18n";
import Marquee from "@/components/Marquee";
import StickyBookingBar from "@/components/StickyBookingBar";
import { useBooking, formatRange } from "@/components/booking/useBooking";
import RangeCalendar from "@/components/booking/RangeCalendar";
import GuestsPanel from "@/components/booking/GuestsPanel";
import RoomCard from "@/components/RoomCard";
import ReviewCard from "@/components/ReviewCard";
import { Reveal, ChapterHeading } from "@/components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className="block overflow-hidden pb-[0.08em]">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.15, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const AMENITY_ICONS = [CarFront, Wifi, Waves, Umbrella, Sun];

function HeroBookingBar() {
    const { lang, t } = useLang();
    const bk = useBooking();
    const field = (icon, label, control, testId, panel) => (
        <div
            className="flex cursor-pointer items-center gap-3 md:px-5"
            data-testid={testId}
            role="button"
            tabIndex={0}
            onClick={() => bk.openPanel(panel)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && bk.openPanel(panel)}
            aria-haspopup="dialog"
            aria-expanded={bk.panel === panel}
        >
            {icon}
            <div className="w-full min-w-0">
                <span className="block text-[10px] font-medium uppercase tracking-[0.18em] text-ink-muted">{label}</span>
                {control}
            </div>
        </div>
    );
    const hasRange = Boolean(bk.arrival && bk.departure);
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="relative z-10 mt-8 w-full max-w-3xl md:mt-12"
        >
            <form
                action="https://rbe.zaviaerp.com/hotel/puntapalmeras"
                method="get"
                onSubmit={bk.validateSubmit}
                data-testid="hero-booking-bar"
                aria-label={t.hero.barAria}
                className="relative grid grid-cols-2 gap-x-4 gap-y-4 rounded-3xl bg-cream/95 p-5 shadow-[0_24px_60px_rgba(45,51,25,0.28)] backdrop-blur md:grid-cols-[1.4fr_1fr_auto] md:items-center md:gap-0 md:divide-x md:divide-sand-border md:rounded-full md:py-3 md:pl-8 md:pr-3"
            >
                <input type="hidden" name="zbe_hotel_id" value="puntapalmeras" />
                <input type="hidden" name="lng" value={lang} />
                <input type="hidden" name="currency" value="MXN" />
                <input type="hidden" name="zbe_arrival" value={bk.arrival} data-testid="hero-checkin-input" />
                <input type="hidden" name="zbe_departure" value={bk.departure} data-testid="hero-checkout-input" />
                <input type="hidden" name="adults" value={bk.adults} data-testid="hero-adults-select" />
                <input type="hidden" name="children" value={bk.children} data-testid="hero-children-select" />
                {field(
                    <CalendarDays className="h-5 w-5 shrink-0 text-olive" strokeWidth={1.5} />,
                    t.hero.dates,
                    <span
                        className={`block w-full min-w-0 truncate text-sm font-medium ${hasRange ? "text-ink" : "text-ink-muted/70"}`}
                        data-testid="hero-dates-display"
                    >
                        {formatRange(bk.arrival, bk.departure, lang) || t.hero.addDates}
                    </span>,
                    "hero-field-dates",
                    "dates",
                )}
                {field(
                    <Users className="h-5 w-5 shrink-0 text-olive" strokeWidth={1.5} />,
                    t.hero.guests,
                    <span className="block w-full min-w-0 truncate text-sm font-medium text-ink" data-testid="hero-guests-display">
                        {t.hero.guestSummary(bk.adults, bk.children)}
                    </span>,
                    "hero-field-guests",
                    "guests",
                )}
                <div className="col-span-2 md:col-span-1 md:pl-5">
                    <button
                        type="submit"
                        data-testid="hero-booking-submit-button"
                        className="w-full whitespace-nowrap rounded-full bg-amber px-8 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-dark hover:shadow-lg"
                    >
                        {t.hero.consult}
                    </button>
                </div>
                {bk.panel === "dates" && (
                    <RangeCalendar start={bk.arrival} end={bk.departure} onChange={bk.setRange} onClose={bk.closePanel} />
                )}
                {bk.panel === "guests" && (
                    <GuestsPanel
                        adults={bk.adults}
                        children={bk.children}
                        onChange={bk.setGuests}
                        onClose={bk.closePanel}
                        className="md:left-auto md:right-24"
                    />
                )}
            </form>
        </motion.div>
    );
}

function Hero() {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "32%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
    const blobY1 = useTransform(scrollYProgress, [0, 1], ["0%", "55%"]);
    const blobY2 = useTransform(scrollYProgress, [0, 1], ["0%", "-35%"]);

    return (
        <section
            id="inicio"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-[100svh] flex-col justify-center"
        >
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <video
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/fotos/hero-poster.jpg"
                aria-hidden="true"
                data-testid="hero-video"
            >
                <source src="/fotos/video-hero-web.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_28%_38%,rgba(0,0,0,0.5),transparent_62%)]" aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-r from-olive-deep/85 via-olive-deep/40 to-olive-deep/10" aria-hidden="true" />
            <div className="grain absolute inset-0" aria-hidden="true" />
            <motion.div
                aria-hidden="true"
                style={{ y: blobY1 }}
                className="absolute -left-32 top-1/4 h-[28rem] w-[28rem] rounded-full bg-amber/30 blur-3xl"
            />
            <motion.div
                aria-hidden="true"
                style={{ y: blobY2 }}
                className="absolute -right-24 bottom-1/4 h-[24rem] w-[24rem] rounded-full bg-olive-deep/40 blur-3xl"
            />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-olive-deep/50 to-transparent" aria-hidden="true" />
            </div>

            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-8 md:px-12 md:pt-28 md:pb-10"
            >
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
                    className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-cream/90 md:text-sm"
                >
                    <span className="h-px w-10 bg-cream/60" aria-hidden="true" />
                    {t.hero.location}
                </motion.p>

                <h1 className="mt-8 text-cream [text-shadow:0_2px_28px_rgba(0,0,0,0.55)]">
                    <MaskedLine delay={0.35} className="font-body text-sm font-medium uppercase tracking-[0.34em] text-cream/90 md:text-base">
                        {t.hero.brand}
                    </MaskedLine>
                    <MaskedLine delay={0.5} className="mt-4 font-display text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-8xl">
                        {t.hero.titleA}
                    </MaskedLine>
                    <MaskedLine delay={0.65} className="font-display text-5xl font-light italic leading-[1.05] tracking-tight text-[#F6E3C5] sm:text-6xl lg:text-8xl">
                        {t.hero.titleB}
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
                    className="mt-6 max-w-xl text-base font-light leading-relaxed text-cream/85 md:mt-8 md:text-lg"
                >
                    {t.hero.sub}
                </motion.p>

            </motion.div>

            <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 md:px-12">
                <HeroBookingBar />
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute inset-x-0 bottom-8 z-10 hidden flex-col items-center gap-2 text-cream/70 md:flex"
            >
                <span className="text-[10px] font-medium uppercase tracking-[0.3em]">{t.hero.scroll}</span>
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
                    <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
                </motion.span>
            </motion.div>

        </section>
    );
}

function Welcome() {
    const { t } = useLang();
    return (
        <section id="nosotros" data-testid="welcome-section" className="scroll-mt-24 py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="grid gap-12 lg:grid-cols-12 lg:gap-20">
                    <div className="lg:col-span-4">
                        <ChapterHeading number={t.welcome.number} label={t.welcome.label} title={<>{t.welcome.titleA} <em className="text-olive">{t.welcome.titleEm}</em> {t.welcome.titleB}</>} />
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal delay={0.15}>
                            <p className="font-display text-2xl font-light leading-relaxed text-ink md:text-[1.9rem] md:leading-[1.5]">
                                {t.welcome.p1a}{" "}
                                <em className="text-amber">{t.welcome.p1em}</em>
                                {t.welcome.p1b}
                            </p>
                            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-ink-muted md:text-lg">
                                {t.welcome.p2}
                            </p>
                        </Reveal>
                        <Reveal delay={0.25} className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {[BeachIcon, BedDouble, Waves].map((Icon, i) => (
                                <div
                                    key={i}
                                    data-testid={`welcome-highlight-${i}`}
                                    className="flex items-center gap-3 rounded-2xl border border-sand-border bg-sand px-5 py-4"
                                >
                                    <Icon className="h-5 w-5 shrink-0 text-olive" strokeWidth={1.5} />
                                    <span className="text-sm font-light text-ink">{t.welcome.highlights[i]}</span>
                                </div>
                            ))}
                        </Reveal>
                    </div>
                </div>
                <div className="mt-16 grid grid-cols-1 gap-6 md:mt-24 md:grid-cols-5">
                    <Reveal delay={0.1} className="md:col-span-3">
                        <img
                            src="/fotos/fachada-nosotros.jpg"
                            alt={t.welcome.imgAlt}
                            loading="lazy"
                            decoding="async"
                            width="1531"
                            height="832"
                            data-testid="welcome-image"
                            className="h-[42vh] w-full rounded-3xl object-cover md:h-[54vh]"
                        />
                    </Reveal>
                    <Reveal delay={0.25} className="md:col-span-2 md:mt-14">
                        <img
                            src="/fotos/terraza-vista-01.jpg"
                            alt="Terraza del hotel con vista a la montaña y palmeras"
                            loading="lazy"
                            decoding="async"
                            width="1200"
                            height="800"
                            data-testid="welcome-image-terraza"
                            className="h-[36vh] w-full rounded-3xl object-cover md:h-[42vh]"
                        />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function AmenitiesBar() {
    const { t } = useLang();
    const item = (Icon, i, prefix) => (
        <div key={`${prefix}-${i}`} data-testid={`amenity-${prefix}-${i}`} className="flex shrink-0 items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-cream text-olive shadow-[0_4px_14px_rgba(45,51,25,0.08)]">
                <Icon className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <span className="whitespace-nowrap text-sm font-medium text-ink">{t.amenities.items[i]}</span>
        </div>
    );
    return (
        <section aria-label={t.amenities.aria} data-testid="amenities-bar" className="overflow-hidden border-y border-sand-border bg-sand">
            <div className="flex w-max animate-marquee gap-10 py-8 md:hidden" data-testid="amenities-marquee">
                {[0, 1].map((copy) => (
                    <div key={copy} className="flex items-center gap-10 pr-10" aria-hidden={copy === 1}>
                        {AMENITY_ICONS.map((Icon, i) => item(Icon, i, `m${copy}`))}
                    </div>
                ))}
            </div>
            <div className="mx-auto hidden max-w-7xl justify-center gap-10 px-6 py-8 md:flex md:px-12">
                {AMENITY_ICONS.map((Icon, i) => (
                    <Reveal key={i} delay={i * 0.07} y={14} className="shrink-0">
                        {item(Icon, i, "d")}
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function Rooms() {
    const { t } = useLang();
    const [roomList, setRoomList] = useState([]);
    useEffect(() => {
        loadRooms().then(setRoomList).catch(() => {});
    }, []);
    return (
        <section id="habitaciones" data-testid="rooms-section" className="scroll-mt-24 py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <ChapterHeading number={t.rooms.number} label={t.rooms.label} title={<>{t.rooms.titleA} <em className="text-olive">{t.rooms.titleEm}</em>{t.rooms.titleB}</>} />
                    <Reveal delay={0.15}>
                        <p className="max-w-sm text-sm font-light leading-relaxed text-ink-muted md:text-base">
                            {t.rooms.intro}
                        </p>
                    </Reveal>
                </div>
                <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {roomList.map((room, i) => (
                        <RoomCard key={room.slug} room={room} index={i} wide={i === 0 || i === roomList.length - 1} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function Reviews() {
    const { t } = useLang();
    return (
        <section id="resenas" data-testid="reviews-section" className="scroll-mt-24 bg-sand py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="grid items-end gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <ChapterHeading number={t.reviews.number} label={t.reviews.label} title={<>{t.reviews.titleA} <em className="text-olive">{t.reviews.titleEm}</em> {t.reviews.titleB}</>} />
                    </div>
                    <Reveal delay={0.15} className="lg:col-span-7 lg:justify-self-end">
                        <div className="flex items-center gap-5" data-testid="reviews-average">
                            <span className="font-display text-7xl font-light text-ink md:text-8xl">{averageRating}</span>
                            <div>
                                <div className="flex gap-1" aria-label={t.reviews.avgAria}>
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star
                                            key={s}
                                            className={`h-5 w-5 ${s <= 4 ? "fill-amber text-amber" : "fill-amber/40 text-amber/40"}`}
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                </div>
                                <p className="mt-2 text-sm font-light text-ink-muted">
                                    {t.reviews.basedOn} <span className="font-medium text-ink">{t.reviews.google}</span>
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
                <div className="hide-scrollbar mt-14 flex snap-x snap-mandatory flex-col gap-6 overflow-x-visible md:flex-row md:overflow-x-auto md:pb-6">
                    {reviews.map((r, i) => (
                        <ReviewCard key={i} review={r} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function Visit() {
    const { t } = useLang();
    const inputCls =
        "w-full rounded-xl border border-cream/25 bg-cream/10 px-4 py-3 text-sm text-cream placeholder-cream/50 outline-none transition-colors duration-300 focus:border-amber-soft focus:bg-cream/15";
    return (
        <section id="contacto" data-testid="visit-section" className="scroll-mt-24 py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="grid overflow-hidden rounded-3xl shadow-[0_20px_60px_rgba(45,51,25,0.12)] lg:grid-cols-2">
                    <div className="grain relative bg-olive p-8 text-cream md:p-14">
                        <ChapterHeading number={t.visit.number} label={t.visit.label} title={<>{t.visit.titleA} <em className="text-amber-soft">{t.visit.titleEm}</em></>} tone="light" />
                        <Reveal delay={0.15} className="relative z-10 mt-10 space-y-5">
                            <p className="flex items-start gap-3 text-sm font-light leading-relaxed text-cream/85">
                                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-amber-soft" strokeWidth={1.5} />
                                Avenida Independencia No. 6, esq. Caguama, Ejido la Fortuna, C.P. 48850, Punta Pérula, Jalisco
                            </p>
                            <p className="flex items-center gap-3 text-sm font-light text-cream/85">
                                <Phone className="h-5 w-5 shrink-0 text-amber-soft" strokeWidth={1.5} />
                                +52 315 126 1885
                            </p>
                            <p className="flex items-center gap-3 text-sm font-light text-cream/85">
                                <Mail className="h-5 w-5 shrink-0 text-amber-soft" strokeWidth={1.5} />
                                info@hotelpuntapalmeras.com
                            </p>
                        </Reveal>
                        <Reveal delay={0.25} className="relative z-10">
                            <form className="mt-10 space-y-4" onSubmit={(e) => e.preventDefault()} data-testid="contact-form">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <input type="text" placeholder={t.visit.name} aria-label={t.visit.name} data-testid="contact-name-input" className={inputCls} />
                                    <input type="email" placeholder={t.visit.email} aria-label={t.visit.email} data-testid="contact-email-input" className={inputCls} />
                                </div>
                                <textarea rows="4" placeholder={t.visit.message} aria-label={t.visit.message} data-testid="contact-message-input" className={inputCls} />
                                <button
                                    type="button"
                                    data-testid="contact-submit-button"
                                    className="rounded-full bg-amber px-9 py-4 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-dark hover:shadow-lg"
                                >
                                    {t.visit.send}
                                </button>
                            </form>
                        </Reveal>
                    </div>
                    <div className="relative min-h-[320px] w-full lg:min-h-full" data-testid="map-embed">
                        <iframe
                            title={t.visit.mapTitle}
                            src="https://www.google.com/maps?q=Hotel+Punta+Palmeras,+Punta+P%C3%A9rula,+Jalisco,+M%C3%A9xico&output=embed"
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            className="absolute inset-0 h-full w-full border-0"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function HomePage() {
    const { t } = useLang();
    useSEO(t.seo.homeTitle, t.seo.homeDesc);
    return (
        <>
            <Hero />
            <StickyBookingBar />
            <Marquee items={t.marquee} />
            <Welcome />
            <AmenitiesBar />
            <Rooms />
            <Reviews />
            <Visit />
        </>
    );
}
